import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import SharedHeader from '../../../../components/SharedHeader';
import DashboardFooter from '../../../../components/DashboardFooter';
import { FaSpinner, FaCheck, FaTimes, FaBell } from 'react-icons/fa';
import './subscription_board.css';

const SubscriptionBoard = () => {
  // # State for tracking function calls and confirmation dialogs
  const [loading, setLoading] = useState({
    endingCheck: false
  });
  const [results, setResults] = useState({
    endingCheck: null
  });
  const [showConfirm, setShowConfirm] = useState({
    endingCheck: false
  });

  // # Initialize Firebase functions with the correct region
  const functions = getFunctions(undefined, 'europe-west1');
  const manualCheckEndingSubscriptions = httpsCallable(functions, 'manualCheckEndingSubscriptions');

  // # Handle confirmation dialog
  const handleConfirmOpen = (type) => {
    setShowConfirm(prev => ({ ...prev, [type]: true }));
  };

  const handleConfirmClose = (type) => {
    setShowConfirm(prev => ({ ...prev, [type]: false }));
  };

  // # Handle cleanup function execution
  const handleCleanup = async (type) => {
    setLoading(prev => ({ ...prev, [type]: true }));
    setResults(prev => ({ ...prev, [type]: null }));
    setShowConfirm(prev => ({ ...prev, [type]: false }));
    
    try {
      let result;
      
      // # Call the appropriate function based on type
      switch (type) {
        case 'endingCheck':
          result = await manualCheckEndingSubscriptions();
          break;
        default:
          throw new Error('Unknown cleanup type');
      }
      
      // # Update results state
      setResults(prev => ({ ...prev, [type]: { 
        success: result.data.success,
        message: result.data.message,
        timestamp: new Date().toISOString()
      }}));
    } catch (error) {
      console.error(`Error executing ${type} cleanup:`, error);
      setResults(prev => ({ ...prev, [type]: { 
        success: false,
        message: error.message || 'An error occurred',
        timestamp: new Date().toISOString()
      }}));
    } finally {
      setLoading(prev => ({ ...prev, [type]: false }));
    }
  };

  // # Render result status
  const renderResult = (type) => {
    const result = results[type];
    
    if (!result) return null;
    
    return (
      <div className={`tech_subc_result ${result.success ? 'tech_subc_success' : 'tech_subc_error'}`}>
        <div className="tech_subc_result-icon">
          {result.success ? <FaCheck /> : <FaTimes />}
        </div>
        <div className="tech_subc_result-content">
          <p className="tech_subc_result-message">{result.message}</p>
          <p className="tech_subc_result-timestamp">
            {new Date(result.timestamp).toLocaleString()}
          </p>
        </div>
      </div>
    );
  };

  // # Render confirmation dialog
  const renderConfirmDialog = (type, title, description) => {
    if (!showConfirm[type]) return null;
    
    return (
      <div className="tech_subc_confirm-overlay">
        <div className="tech_subc_confirm-dialog">
          <h3>{title}</h3>
          <p>{description}</p>
          <div className="tech_subc_confirm-actions">
            <button 
              className="tech_subc_confirm-cancel" 
              onClick={() => handleConfirmClose(type)}
            >
              Cancel
            </button>
            <button 
              className="tech_subc_confirm-proceed" 
              onClick={() => handleCleanup(type)}
            >
              Proceed
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="tech_subc_container">
      <SharedHeader />
      
      <main className="tech_subc_content">
        <div className="tech_subc_header">
          <h1>Subscription Management</h1>
          <p>Manage subscription notifications and cleanup operations</p>
        </div>

        <div className="tech_subc_cards">
          {/* Ending Subscriptions Check Card */}
          <div className="tech_subc_card">
            <div className="tech_subc_card-header">
              <FaBell className="tech_subc_card-icon" />
              <h2>Check Ending Subscriptions</h2>
            </div>
            <div className="tech_subc_card-body">
              <p>
                Checks for active subscriptions ending within the next 4 days and sends notifications
                to users. This helps users stay informed about their subscription status.
              </p>
              <p className="tech_subc_card-note">
                Automatically runs daily at 2:00 AM (Cairo time).
              </p>
              {renderResult('endingCheck')}
            </div>
            <div className="tech_subc_card-footer">
              <button 
                className="tech_subc_button"
                onClick={() => handleConfirmOpen('endingCheck')}
                disabled={loading.endingCheck}
              >
                {loading.endingCheck ? <FaSpinner className="tech_subc_spinner" /> : 'Check Now'}
              </button>
            </div>
          </div>
        </div>
      </main>

      <DashboardFooter />

      {/* Confirmation Dialog */}
      {renderConfirmDialog(
        'endingCheck',
        'Confirm Ending Subscriptions Check',
        'This will check all active subscriptions and send notifications to users whose subscriptions are ending within 4 days. Are you sure you want to proceed?'
      )}
    </div>
  );
};

export default SubscriptionBoard;
